import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Typography, type TypographyProps } from '@mui/material';
import { useResize } from './useResize.hook';
import {
  wrapperStyles, childrenStyles, block, spreaderStyles,
  sizerWrapperStyles,
} from './constants';

export interface ShiitakeProps extends TypographyProps {
  readonly lines: number;
  readonly overflowNode?: React.ReactNode;
}

// From https://github.com/bsidelinger912/shiitake
export const Shiitake: React.FC<ShiitakeProps> = ({
  children: componentChildren, lines, overflowNode = null, ...restProps
}) => {
  const allChildren = (typeof componentChildren === 'string') ? componentChildren : '';

  const sizerEl = useRef<HTMLSpanElement>(null);
  const spreaderEl = useRef<HTMLSpanElement>(null);
  const testChildrenEl = useRef<HTMLSpanElement>(null);
  const handlingResize = useRef(true);
  const searchStart = useRef(0);
  const searchEnd = useRef(allChildren.length);

  const [testChildren, setTestChildren] = useState('');
  const [children, setChildren] = useState(allChildren);
  const [lastCalculatedWidth, setLastCalculatedWidth] = useState(-1);

  const testChildrenRange = useCallback((start: number, end: number) => {
    searchStart.current = start;
    searchEnd.current = end;

    setTestChildren(allChildren.substring(0, end));
  }, [allChildren]);

  const calculationComplete = useCallback(() => {
    let newChildren = allChildren;

    // are we actually trimming?
    if (testChildren.length < allChildren.length) {
      newChildren = testChildren.split(' ').slice(0, -1).join(' ');
    }

    handlingResize.current = false;
    setChildren(newChildren);
  }, [allChildren, testChildren]);

  const checkHeight = useCallback(() => {
    const contentHeight = testChildrenEl.current.offsetHeight;
    const halfWay = Math.round((searchEnd.current - searchStart.current) / 2);
    const targetHeight = sizerEl.current ? sizerEl.current.offsetHeight : undefined;
    const linear = searchEnd.current - searchStart.current < 6;

    // do we need to trim?
    if (contentHeight > targetHeight) {
      // chunk/ trim down
      if (linear) {
        testChildrenRange(testChildren.length, testChildren.length - 1);
      } else {
        testChildrenRange(searchStart.current, searchEnd.current - halfWay);
      }

    // we've used all the characters in a window expand situation
    } else if (testChildren.length === allChildren.length) {
      calculationComplete();
    } else if (linear) {
      // if we just got here by decrementing one, we're good
      if (searchStart.current > searchEnd.current) {
        calculationComplete();
      } else {
        // window grew, increment up one
        testChildrenRange(testChildren.length, testChildren.length + 1);
      }
    } else {
      // chunk up, still in binary search mode
      testChildrenRange(searchEnd.current, searchEnd.current + halfWay);
    }
  }, [allChildren.length, calculationComplete, testChildren.length, testChildrenRange]);

  const startCalculation = useCallback(() => {
    searchStart.current = 0;
    searchEnd.current = allChildren.length;
    setLastCalculatedWidth(spreaderEl.current.offsetWidth);
    handlingResize.current = true;
    setTestChildren(allChildren);
  }, [allChildren]);

  const recalculate = useCallback(() => {
    // this will kick of a sequence mimicing an initial calculation, this is necessary because if we're currently showing
    // all the children (no need to truncate) then the effect below to check height won't kick in which it needs too.
    setTestChildren('');
  }, []);

  const handleResize = useCallback(() => {
    if (!spreaderEl.current) {
      return;
    }

    if (spreaderEl.current.offsetWidth !== lastCalculatedWidth && !handlingResize.current) {
      recalculate();
    }
  }, [recalculate, lastCalculatedWidth]);

  useResize(spreaderEl.current, handleResize);

  useEffect(() => {
    if (testChildren === '' && sizerEl.current) {
      startCalculation();
    } else {
      checkHeight();
    }
  }, [testChildren, checkHeight, startCalculation]);

  useEffect(() => {
    // this will skip this effect on the first render
    if (testChildren !== '') {
      recalculate();
    }
  }, [lines, allChildren, recalculate]); // eslint-disable-line react-hooks/exhaustive-deps

  const vertSpacers = [];
  for (let i = 0; i < lines; i += 1) {
    vertSpacers.push((
      <span
        style={block}
        key={i}
      >
        W
      </span>
    ));
  }

  const maxHeight = `${sizerEl.current ? sizerEl.current.offsetHeight : 0}px`;
  const overflow = (testChildren.length < allChildren.length) ? overflowNode : null;

  return (
    <Typography {...restProps}>
      <span style={{ ...wrapperStyles as any, maxHeight }}>
        <span
          className="shiitake-children"
          style={childrenStyles}
        >
          {children}

          {overflow}
        </span>

        <span
          ref={spreaderEl}
          style={spreaderStyles}
          aria-hidden="true"
        >
          {allChildren}
        </span>

        <span
          style={sizerWrapperStyles as any}
          aria-hidden="true"
        >
          <span
            ref={sizerEl}
            style={block}
          >
            {vertSpacers}
          </span>

          <span
            className="shiitake-test-children"
            ref={testChildrenEl}
            style={block}
          >
            {testChildren}

            {overflow}
          </span>
        </span>
      </span>
    </Typography>
  );
};
